import { useEffect, useState, useCallback } from "react";
import type { User } from "./types";
import { setToken, getToken } from "./api";
import { queryClient } from "./queryClient";
import { createAuthApiClient, readStoredSession } from "@/features/auth/api";
import { tokenScope } from "./session-scope";
import { disconnectWebSockets } from "./ws";
import { clearAuthStorage, retireLegacyAuthStorage } from "./storage-migration";
import { clearWebPushForSessionChange, disableWebPush } from '@/features/notifications/register';

const SESSION_KEY = "ailechat:session";
const SESSION_EVENT = "ailechat:session-changed";

export interface Session {
  user: User;
  token: string;
  tenantId?: string;
}

export function sessionScope(session: Session | null): string {
  return session ? `${session.user.id}:${tokenScope(session.token)?.tenantId || session.tenantId || ""}` : "signed-out";
}

export function getSession(): Session | null {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as Session;
    return session?.user?.id && typeof session.token === "string" ? {...session,tenantId:tokenScope(session.token)?.tenantId || session.tenantId} : null;
  } catch {
    return null;
  }
}

export function setSession(session: Session | null): void {
  if (typeof localStorage === "undefined") return;
  const previous = getSession();
  if (sessionScope(previous) !== sessionScope(session)) {
    disconnectWebSockets();
    if (previous) void clearWebPushForSessionChange(previous.token).catch(() => undefined);
  }
  if (session) {
    retireLegacyAuthStorage();
    session={...session,tenantId:tokenScope(session.token)?.tenantId || session.tenantId};
    if (previous?.token !== session.token || previous?.user.id !== session.user.id) queryClient.clear();
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    setToken(session.token);
  } else {
    clearAuthStorage();
    queryClient.clear();
  }
  window.dispatchEvent(new CustomEvent(SESSION_EVENT, { detail: session }));
}

export function useSession(): Session | null {
  const [session, setLocal] = useState<Session | null>(() => getSession());
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<Session | null>).detail;
      setLocal(detail ?? null);
    };
    const onStorage = (event: StorageEvent) => { if (event.key === SESSION_KEY || event.key === null) { const next = getSession(); if (sessionScope(next)!==sessionScope(session)) disconnectWebSockets(); if (!next || next.token !== session?.token || next.user.id !== session?.user.id) queryClient.clear(); setLocal(next); } };
    window.addEventListener(SESSION_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    // Sync token if session existed
    if (session?.token && !getToken()) setToken(session.token);
    return () => { window.removeEventListener(SESSION_EVENT, onChange); window.removeEventListener("storage", onStorage); };
  }, [session]);
  return session;
}

export function useLogout() {
  const logout = useCallback(async () => {
    const stored = readStoredSession();
    await disableWebPush().catch(() => undefined);
    setSession(null);
    if (stored?.refresh_token) await createAuthApiClient().logout(stored.refresh_token).catch(() => {});
  }, []);
  return { logout };
}
